import { supabase } from '../supabase';
import { DEFAULT_THEME, normalizeTheme, type DesignTheme } from './theme';

export interface DesignPreset {
  id: string;
  name: string;
  theme: DesignTheme;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const CACHE_KEY = 'sk_active_theme';

// ── Local cache (flash-free boot) ────────────────────────────────────────────

/** Read the last-applied theme synchronously (used before the DB responds). */
export function readCachedTheme(): DesignTheme | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return normalizeTheme(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeCachedTheme(theme: DesignTheme): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(theme));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

// ── Remote presets ───────────────────────────────────────────────────────────

function rowToPreset(row: Record<string, unknown>): DesignPreset {
  return {
    id: String(row.id),
    name: String(row.name ?? 'Untitled'),
    theme: normalizeTheme(row.theme, String(row.name ?? '')),
    is_active: !!row.is_active,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

/** All presets, newest first. Returns [] if the table doesn't exist yet. */
export async function listPresets(): Promise<DesignPreset[]> {
  const { data, error } = await supabase
    .from('design_presets')
    .select('*')
    .order('created_at', { ascending: false });
  if (error || !data) return [];
  return data.map((d) => rowToPreset(d as Record<string, unknown>));
}

/** The active theme from the DB, or null if none / unavailable. */
export async function fetchActiveTheme(): Promise<DesignTheme | null> {
  const { data, error } = await supabase
    .from('design_presets')
    .select('theme, name')
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return normalizeTheme((data as Record<string, unknown>).theme, String((data as Record<string, unknown>).name ?? ''));
}

/** Insert a new preset. When activate is true it becomes the active theme. */
export async function createPreset(
  name: string,
  theme: DesignTheme,
  activate = false,
): Promise<DesignPreset | null> {
  const payload = { name, theme: { ...theme, name } };
  const { data, error } = await supabase
    .from('design_presets')
    .insert(payload)
    .select('*')
    .maybeSingle();
  if (error || !data) return null;
  const preset = rowToPreset(data as Record<string, unknown>);
  if (activate) await setActivePreset(preset.id);
  return preset;
}

/** Make a preset the single active theme (clears the flag on all others). */
export async function setActivePreset(id: string): Promise<boolean> {
  // Two-step keeps the "only one active" partial-unique index happy.
  const clear = await supabase
    .from('design_presets')
    .update({ is_active: false })
    .neq('id', id);
  if (clear.error) return false;
  const set = await supabase
    .from('design_presets')
    .update({ is_active: true, updated_at: new Date().toISOString() })
    .eq('id', id);
  return !set.error;
}

export async function deletePreset(id: string): Promise<boolean> {
  const { error } = await supabase.from('design_presets').delete().eq('id', id);
  return !error;
}

/** Ensure a default preset exists and is active. Returns its theme. */
export async function ensureDefaultPreset(): Promise<DesignTheme> {
  const presets = await listPresets();
  if (presets.length === 0) {
    const created = await createPreset(DEFAULT_THEME.name, DEFAULT_THEME, true);
    return created?.theme ?? DEFAULT_THEME;
  }
  const active = presets.find((p) => p.is_active);
  return active?.theme ?? DEFAULT_THEME;
}
